import PropTypes from 'prop-types'
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'
import { createAivyAssistantIntro } from './aivy-assistant-intro'

/**
 * <AivyAssistantIntro />
 *
 * Props:
 *  - autoplay   (default true)
 *  - loop       (default false)  – nach Ende neu starten
 *  - idle       (default true)   – nach Ende weiter blinzeln/schweben
 *  - background (default "#F6F7F9") – auch "transparent" möglich
 *  - delay      (default 0.2)    – Sekunden bis Start
 *  - onComplete ()               – feuert einmal bei t = 10 s
 *
 * Ref: { play, pause, replay, seek(t) }
 */
const AivyAssistantIntro = forwardRef(function AivyAssistantIntro(
  {
    autoplay = true,
    loop = false,
    idle = true,
    background = '#F6F7F9',
    delay = 0.2,
    onComplete,
    className,
    style
  },
  ref
) {
  const hostRef = useRef(null)
  const instRef = useRef(null)
  const onCompleteRef = useRef(onComplete)
  onCompleteRef.current = onComplete

  useEffect(() => {
    const inst = createAivyAssistantIntro(hostRef.current, {
      autoplay,
      loop,
      idle,
      background,
      delay,
      onComplete: () => onCompleteRef.current && onCompleteRef.current()
    })
    instRef.current = inst
    return () => inst.destroy()
    // Optionen gelten ab Mount; Hintergrund wird live aktualisiert.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    instRef.current && instRef.current.setBackground(background)
  }, [background])

  useImperativeHandle(ref, () => ({
    play: () => instRef.current?.play(),
    pause: () => instRef.current?.pause(),
    replay: () => instRef.current?.replay(),
    seek: (t) => instRef.current?.seek(t)
  }))

  return (
    <div
      ref={hostRef}
      className={className}
      style={{ borderRadius: 20, overflow: 'hidden', ...style }}
    />
  )
})

AivyAssistantIntro.propTypes = {
  autoplay: PropTypes.bool,
  loop: PropTypes.bool,
  idle: PropTypes.bool,
  background: PropTypes.string,
  delay: PropTypes.number,
  onComplete: PropTypes.func,
  className: PropTypes.string,
  style: PropTypes.object
}

export default AivyAssistantIntro
